import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Check, Users, Building2, ChevronRight, AlertCircle } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import type { UserTeam } from '../../utils/types';

interface TeamSwitchModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const TeamSwitchModal: React.FC<TeamSwitchModalProps> = ({ isOpen, onClose }) => {
    const { switchTeam, fetchUserTeams } = useAuthStore();
    const [teams, setTeams] = useState<UserTeam[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [switchingTeamId, setSwitchingTeamId] = useState<string | null>(null);

    // Use refs to avoid stale closure issues
    const isOpenRef = useRef(isOpen);
    const hasLoadedRef = useRef(false);

    // Update ref when isOpen changes
    useEffect(() => {
        isOpenRef.current = isOpen;

        // Reset hasLoaded when modal closes
        if (!isOpen) {
            hasLoadedRef.current = false;
        }
    }, [isOpen]);

    // Stable loadTeams function using useCallback
    const loadTeams = useCallback(async () => {


        // Double-check if modal is still open
        if (!isOpenRef.current) {
            return;
        }

        if (hasLoadedRef.current) {
            return;
        }

        try {
            setLoading(true);
            setError(null);

            if (!fetchUserTeams) {
                throw new Error('fetchUserTeams function not available in auth store');
            }

            const userTeams = await fetchUserTeams();

            // Check again if modal is still open after async call
            if (!isOpenRef.current) {
                return;
            }


            if (!userTeams) {
                setTeams([]);
                hasLoadedRef.current = true;
                return;
            }

            if (Array.isArray(userTeams)) {
                setTeams(userTeams);
                hasLoadedRef.current = true;
            } else {
                setTeams([]);
                hasLoadedRef.current = true;
            }
        } catch (error) {

            // Only update error state if modal is still open
            if (isOpenRef.current) {
                setError(error instanceof Error ? error.message : 'Failed to load teams');
                setTeams([]);
            }
        } finally {
            // Only update loading state if modal is still open
            if (isOpenRef.current) {
                setLoading(false);
            }
        }
    }, [fetchUserTeams]);

    useEffect(() => {
        if (isOpen && !hasLoadedRef.current) {
            loadTeams();
        } else if (!isOpen) {
            // Reset states when modal closes
            setTeams([]);
            setLoading(false);
            setError(null);
            setSwitchingTeamId(null);
        }
    }, [isOpen, loadTeams]);

    const handleTeamSwitch = async (teamId: string) => {
        if (switchingTeamId) return;

        try {
            setSwitchingTeamId(teamId);
            setError(null);
            await switchTeam(teamId);
            onClose();
        } catch (error) {
            setError(error instanceof Error ? error.message : 'Failed to switch team');
            setSwitchingTeamId(null);
        }
    };

    const getPlanBadgeColor = (plan: string) => {
        switch (plan.toLowerCase()) {
            case 'free':
                return 'bg-gray-100 text-gray-700';
            case 'pro':
                return 'bg-primary-100 text-primary-700';
            case 'enterprise':
                return 'bg-purple-100 text-purple-700';
            default:
                return 'bg-gray-100 text-gray-700';
        }
    };

    const getRoleColor = (role: string) => {
        switch (role.toLowerCase()) {
            case 'owner':
                return 'text-red-600';
            case 'admin':
                return 'text-orange-600';
            default:
                return 'text-gray-600';
        }
    };


    if (!isOpen) {
        return null;
    }

    return (
        <div
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
            onClick={(e) => {
                if (e.target === e.currentTarget) {
                    onClose();
                }
            }}
        >
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[80vh] overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <div>
                        <h2 className="text-xl font-semibold text-gray-900">Switch Team</h2>
                        <p className="text-sm text-gray-600 mt-1">Choose which team to work with</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="overflow-y-auto max-h-96">
                    {loading ? (
                        <div className="p-8 text-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                            <p className="text-gray-600 mt-4">Loading teams...</p>
                        </div>
                    ) : error ? (
                        <div className="p-8 text-center">
                            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
                            <p className="text-red-600 mb-4">{error}</p>
                            <button
                                onClick={() => {
                                    hasLoadedRef.current = false;
                                    loadTeams();
                                }}
                                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                            >
                                Try Again
                            </button>
                        </div>
                    ) : teams.length === 0 ? (
                        <div className="p-8 text-center">
                            <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                            <p className="text-gray-600 mb-2">No teams available</p>
                            <p className="text-sm text-gray-500">You may need to be invited to teams or create one</p>
                        </div>
                    ) : (
                        <div className="p-4 space-y-2">
                            {teams.map((team) => (
                                <button
                                    key={team.id}
                                    onClick={() => !team.isCurrentTeam && handleTeamSwitch(team.id)}
                                    disabled={team.isCurrentTeam || switchingTeamId === team.id}
                                    className={`w-full flex items-center justify-between p-4 rounded-xl transition-all duration-200 ${team.isCurrentTeam
                                            ? 'bg-primary-50 border-2 border-primary-200 cursor-default'
                                            : 'hover:bg-gray-50 border border-gray-200 hover:border-gray-300 cursor-pointer'
                                        } ${switchingTeamId === team.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                    <div className="flex items-center space-x-3">
                                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${team.type === 'personal' ? 'bg-green-100' : 'bg-primary-100'
                                            }`}>
                                            {team.type === 'personal' ? (
                                                <Users className={`w-5 h-5 ${team.type === 'personal' ? 'text-green-600' : 'text-primary-600'
                                                    }`} />
                                            ) : (
                                                <Building2 className="w-5 h-5 text-primary-600" />
                                            )}
                                        </div>
                                        <div className="text-left">
                                            <div className="flex items-center space-x-2">
                                                <h3 className="font-medium text-gray-900">{team.name}</h3>
                                                {team.isCurrentTeam && (
                                                    <Check className="w-4 h-4 text-primary-600" />
                                                )}
                                            </div>
                                            <div className="flex items-center space-x-2 mt-1">
                                                <span className={`text-xs font-medium ${getRoleColor(team.role)}`}>
                                                    {team.role}
                                                </span>
                                                <span className={`text-xs px-2 py-1 rounded-full font-medium ${getPlanBadgeColor(team.plan)}`}>
                                                    {team.plan.toUpperCase()}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex items-center">
                                        {switchingTeamId === team.id && (
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600 mr-2"></div>
                                        )}
                                        {!team.isCurrentTeam && (
                                            <ChevronRight className="w-4 h-4 text-gray-400" />
                                        )}
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-gray-200 bg-gray-50">
                    <p className="text-xs text-gray-500 text-center">
                        Teams you're a member of will appear here
                    </p>
                </div>
            </div>
        </div>
    );
};

export default TeamSwitchModal;